import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  isLocale,
  matchBrowserLocale,
  readStoredLocale,
} from "./locales.js";
import { createTranslator } from "./translate.js";
import enDict from "./messages/en.json";

// Auto-discovers whatever locale catalogs actually exist under messages/ —
// today that's just en.json. Adding messages/es.json later requires no
// changes here; it's picked up automatically. Locales listed in
// SUPPORTED_LOCALES with no matching file fall back to English (see
// loadDictionary below) rather than 404ing or crashing.
const catalogLoaders = import.meta.glob(["./messages/*.json", "!./messages/en.json"]);

// One level deep is enough for this catalog's shape (namespace -> flat
// string keys); a generic recursive merge isn't needed here.
function mergeOntoEnglish(dict) {
  const merged = { ...enDict };
  for (const ns of Object.keys(dict)) {
    merged[ns] = { ...enDict[ns], ...dict[ns] };
  }
  return merged;
}

async function loadDictionary(locale) {
  if (locale === DEFAULT_LOCALE) return enDict; // already loaded statically, below
  const load = catalogLoaders[`./messages/${locale}.json`];
  if (!load) return enDict; // no catalog for this locale yet — fall back to English
  const mod = await load();
  // Merged onto English so a catalog missing newer keys (e.g. translated
  // before a landing-page copy change) shows English for just those keys
  // instead of the raw "landing.someKey" fallback string.
  return mergeOntoEnglish(mod.default ?? mod);
}

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  t: createTranslator(enDict),
  setLocale: () => {},
});

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(
    () => readStoredLocale() ?? matchBrowserLocale(navigator.languages ?? [navigator.language]),
  );
  const [dict, setDict] = useState(enDict);

  useEffect(() => {
    let cancelled = false;
    loadDictionary(locale).then((d) => {
      if (!cancelled) setDict(d);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  function setLocale(next) {
    if (!isLocale(next)) return;
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      /* ignore unavailable storage */
    }
    setLocaleState(next);
  }

  const value = useMemo(
    () => ({ locale, t: createTranslator(dict), setLocale }),
    [locale, dict],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT() {
  return useContext(I18nContext).t;
}
